import React, { ComponentType, Fragment, FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'
import tinyColor from 'tinycolor2'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Divider } from '../../atoms/divider/Divider'
import { IconBadge } from '../../atoms/iconBadge/IconBadge'
import { AlertLoudIcon } from '../../atoms/icons/AlertLoudIcon'
import { SparkleGradient } from '../../atoms/icons/SparkleGradient'
import { ToDoChecklistIcon } from '../../atoms/icons/ToDoChecklistIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { InlineBanner } from '../../molecules/inlineBanner/InlineBanner'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type RiskAlertRecommendedAction = {
  id: string
  title: ReactNode
  subtitle?: ReactNode
  Icon?: ComponentType<{ size?: number | string; fillColor?: string }>
  onPress: () => void
}

export type RiskAlertCardProps = {
  headline: ReactNode
  description?: ReactNode
  isAIGenerated?: boolean
  recommendedActions?: RiskAlertRecommendedAction[]
  testID?: string
}

const Card = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundError,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: tinyColor(colors.borderError).setAlpha(0.2).toRgbString(),
  borderRadius: spacing['16px'],
  padding: spacing['24px'],
  gap: spacing['24px'],
}))

const Intro = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['8px'],
}))

const Description = styled.View(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: spacing['4px'],
}))

const ActionsSection = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const ActionsList = styled.View(({ theme: { colors, spacing } }) => ({
  backgroundColor: colors.backgroundPrimary,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: colors.borderDefault,
  borderRadius: spacing['12px'],
  overflow: 'hidden',
}))

const ActionRow = styled(PressableOpacity)(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['12px'],
  minHeight: 64,
  padding: spacing['16px'],
}))

const ActionIcon = styled.View(({ theme: { colors } }) => ({
  width: 40,
  height: 40,
  borderRadius: 100,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.components.badge.alert.background,
}))

const ActionDetails = styled.View(({ theme: { spacing } }) => ({
  flex: 1,
  gap: spacing['2px'],
}))

export const RiskAlertCard: FunctionComponent<RiskAlertCardProps> = ({
  headline,
  description,
  isAIGenerated = false,
  recommendedActions = [],
  testID,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()

  return (
    <Card testID={testID ?? tID('RiskAlertCard')}>
      <Intro>
        <InlineBanner
          text={formatMessage({
            defaultMessage: 'Risk alert',
            description: 'Label at the top of the risk alert card',
          })}
          icon={<AlertLoudIcon width={20} height={17.5} fillColor={colors.iconError} />}
          iconMarginRight={8}
          backgroundColor='transparent'
          textColor={colors.textError}
          padding='0px'
          marginBottom='0px'
          borderRadius='0px'
        />
        <Title testID={tID('RiskAlertCard-headline')} text={headline} size={TitleSize.MEDIUM} />
        {description && (
          <Description>
            <BodyText text={description} size={BodyTextSize.DEFAULT} color={colors.textSecondary} />
            {isAIGenerated && (
              <IconBadge
                text={formatMessage({
                  defaultMessage: 'AI Generated',
                  description: 'Badge to let user know that the associated section is generated by AI',
                })}
                frontIcon={<SparkleGradient size={18} color={colors.textSecondary} />}
                textSizePx={12}
                bold
                preserveCase
                backgroundColor='transparent'
              />
            )}
          </Description>
        )}
      </Intro>
      {recommendedActions.length > 0 && (
        <ActionsSection>
          <Title
            text={formatMessage({
              defaultMessage: 'Recommended actions',
              description: 'Heading above the list of things a provider should do next for a member',
            })}
            size={TitleSize.SMALL}
          />
          <ActionsList>
            {recommendedActions.map(({ id, title, subtitle, Icon = ToDoChecklistIcon, onPress }, index) => (
              <Fragment key={id}>
                {index > 0 && <Divider />}
                <ActionRow
                  onPress={onPress}
                  pressedOpacity={1}
                  pressedBackgroundColor={colors.backgroundSecondary}
                  accessibilityRole='button'
                  testID={tID(`RiskAlertCard-action-${id}`)}
                >
                  <ActionIcon>
                    <Icon size={24} fillColor={colors.textError} />
                  </ActionIcon>
                  <ActionDetails>
                    <Title text={title} size={TitleSize.XSMALL} />
                    {subtitle && <BodyText text={subtitle} size={BodyTextSize.SMALL} color={colors.textSecondary} />}
                  </ActionDetails>
                </ActionRow>
              </Fragment>
            ))}
          </ActionsList>
        </ActionsSection>
      )}
    </Card>
  )
}
